import React, { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ChevronRight } from "lucide-react";
import { AssessmentLeaderboardItem } from "../../../../axios/quiz";
import { formatAssessmentUserScorePoints } from "../../../../utils/assessmentScore";
import { sortAssessmentLeaderboard } from "../../assessment-contest-utils";
import {
  topPerformerLeftStyle,
  topPerformerNameStyle,
  topPerformerRankStyle,
  topPerformerRowStyle,
  topPerformerScoreStyle,
  topPerformersCardStyle,
  topPerformersEmptyStyle,
  topPerformersLinkStyle,
  topPerformersListStyle,
  topPerformersTitleStyle,
} from "./styles";

interface AssessmentOverviewTopPerformersProps {
  performers: AssessmentLeaderboardItem[];
  maxScore?: number;
  previewCount?: number;
}

export const AssessmentOverviewTopPerformers = ({
  performers,
  maxScore = 0,
  previewCount = 3,
}: AssessmentOverviewTopPerformersProps) => {
  const [showAll, setShowAll] = useState(false);

  const rankedPerformers = useMemo(() => {
    let previousRankingKey: string | null = null;
    let previousRank = 0;

    return sortAssessmentLeaderboard(performers, null).map((item, index) => {
      const itemRankingKey = `${item.user_score ?? item.score}:${item.mandatory_score ?? 0}`;
      const rank =
        previousRankingKey === itemRankingKey ? previousRank : index + 1;
      previousRankingKey = itemRankingKey;
      previousRank = rank;

      return {
        ...item,
        rank,
      };
    });
  }, [performers]);

  const visiblePerformers = showAll
    ? rankedPerformers
    : rankedPerformers.slice(0, previewCount);
  const hasMore = rankedPerformers.length > previewCount;

  return (
    <Box sx={topPerformersCardStyle}>
      <Typography sx={topPerformersTitleStyle}>Top performers</Typography>
      <Box sx={topPerformersListStyle}>
        {visiblePerformers.length === 0 ? (
          <Typography sx={topPerformersEmptyStyle}>
            No leaderboard data yet.
          </Typography>
        ) : (
          visiblePerformers.map((item) => {
            const userScore =
              typeof item.user_score === "number" ? item.user_score : item.score;
            const gamificationBonus =
              typeof item.gamification_bonus_score === "number"
                ? item.gamification_bonus_score
                : item.bonus_score ?? 0;

            return (
              <Box key={item.user_id} sx={topPerformerRowStyle}>
                <Box sx={topPerformerLeftStyle}>
                  <Box sx={topPerformerRankStyle(item.rank)}>{item.rank}</Box>
                  <Typography noWrap title={item.name} sx={topPerformerNameStyle}>
                    {item.name}
                  </Typography>
                </Box>
                <Typography sx={topPerformerScoreStyle}>
                  {formatAssessmentUserScorePoints(userScore, gamificationBonus)}
                  {maxScore > 0
                    ? ` / ${maxScore}`
                    : ""}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
      {hasMore ? (
        <Typography sx={topPerformersLinkStyle} onClick={() => setShowAll((prev) => !prev)}>
          {showAll ? "Show less" : "View leaderboard"}
          <ChevronRight
            size={14}
            style={{
              transform: showAll ? "rotate(90deg)" : "none",
              transition: "transform 0.2s ease",
            }}
          />
        </Typography>
      ) : null}
    </Box>
  );
};

export default AssessmentOverviewTopPerformers;
